// RiskCategory Configuration - UNIFIED APPROACH
// Manages risk categories for risk assessment categorization
// Based on Prisma RiskCategory model schema

import { createTrackingConfig } from '../../../components/generic/labels';
import { tableConfig, uiConfig } from '../../../config/appConfig';

export const riskCategoryConfig = {
  entityName: 'RiskCategory',
  
  // Backend API Configuration
  api: {
    baseEndpoint: '/risk-category',
    endpoints: {
      list: '',
      create: '',
      update: (id) => `/${id}`,
      delete: (id) => `/${id}`,
      getById: (id) => `/${id}`,
      // RiskCategory specific endpoints
      activate: (id) => `/${id}/activate`,
      deactivate: (id) => `/${id}/deactivate`,
      reorder: '/reorder'
    },
    lookupEndpoints: {
      // Risk categories typically don't have lookup dependencies
      // but you could add risk types, severity levels, etc.
    }
  },
  
  // UI Configuration - Using centralized app config
  ui: {
    drawerWidth: '600px', // Standard width for risk categories
    defaultPageSize: tableConfig.defaultPageSize, // From app config: 10
    pageSizeOptions: tableConfig.pageSizeOptions, // From app config: [10, 25, 50, 100]
    defaultTableDensity: tableConfig.defaultDensity, // From app config: 'normal'
    requiredFieldStyle: uiConfig.requiredFieldStyle // From app config: 'badge'
  },
  
  // Service configuration
  service: {
    name: 'riskCategoryService',
    methods: {
      getAll: 'getAllRiskCategories',
      getById: 'getRiskCategoryById', 
      create: 'createRiskCategory',
      update: 'updateRiskCategory',
      delete: 'deleteRiskCategory',
      validate: 'validateData'
    }
  },

  // UNIFIED FIELD CONFIGURATION
  fields: [
    // Basic Information
    { 
      name: 'id', 
      label: 'ID',
      type: 'hidden',
      table: false,
      filterable: false
    },
    { 
      name: 'name', 
      label: 'Risk Category Name', 
      type: 'text', 
      required: true,
      placeholder: 'Enter risk category name (e.g., Operational, Compliance, Financial)',
      table: { header: 'Risk Category', width: '200px', sortable: true },
      filterable: true,
      validation: { 
        required: true, 
        minLength: 2, 
        maxLength: 100,
        message: 'Risk category name required'
      }
    },
    { 
      name: 'isActive', 
      label: 'Active', 
      type: 'switch', 
      required: false,
      defaultValue: true,
      table: { 
        header: 'Status', 
        width: '100px', 
        sortable: true,
        cellRenderer: 'boolean'
      },
      filterable: true,
      validation: { required: false },
      switchLabel: 'Risk category is active and available for use',
      description: 'Inactive risk categories cannot be used for risk assessments'
    },
    { 
      name: 'record_order', 
      label: 'Display Order', 
      type: 'number', 
      required: false,
      placeholder: 'Enter display order (optional)',
      table: { header: 'Order', width: '80px', sortable: true },
      filterable: false,
      validation: { 
        required: false,
        min: 1,
        max: 999,
        integer: true,
        message: 'Display order must be a whole number between 1 and 999'
      },
      helperText: 'Controls the order in dropdown lists and displays',
      step: 1,
      decimals: 0,
      allowNegative: false
    }
  ],

  // Table Configuration - Dynamic getter that generates columns from fields
  get tableColumns() {
    return this.fields
      .filter(field => field.table !== false && !field.hidden)
      .map(field => ({
        key: field.name,
        header: field.table?.header || field.label,
        width: field.table?.width || 'auto',
        sortable: field.table?.sortable !== false,
        displayField: field.table?.displayField || field.name,
        cellRenderer: field.table?.cellRenderer || 'default'
      }));
  },

  // Filter Configuration
  filterableFields: ['name', 'isActive'],

  // Filter Groups for Basic/Advanced tabs
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic Filters',
      fields: ['name', 'isActive']
    },
  ],

  // Custom Sort Orders
  customSortOrders: {
    isActive: [true, false], // Active risk categories first
    record_order: 'asc' // Ascending order by default
  },

  // Messages Configuration
  messages: {
    loading: 'Loading risk categories...',
    noData: 'No risk categories found. Click "Add Risk Category" to create the first risk category.',
    loadError: 'Failed to load risk categories',
    saveSuccess: 'Risk category saved successfully!',
    saveError: 'Failed to save risk category',
    deleteSuccess: 'Risk category deleted successfully!',
    deleteError: 'Failed to delete risk category',
    deleteConfirm: 'Are you sure you want to delete this risk category? This action cannot be undone and may affect risk assessments that reference this category.',
    activateSuccess: 'Risk category activated successfully!',
    deactivateSuccess: 'Risk category deactivated successfully!'
  },

  // Form Layout Configuration
  layout: {
    mode: 'sections',
    sections: [
      {
        id: 'basic',
        title: 'Risk Category Information',
        icon: '⚠️',
        columns: 2,
        fields: [
          { name: 'name' },
          { name: 'isActive' },
          { name: 'record_order' }
        ]
      }
    ]
  },

  // Tracking Section Configuration
  trackingSection: createTrackingConfig('RiskCategory'),

  // Labels Configuration
  labels: {
    // Using predefined labels + custom overrides
    singular: 'Risk Category',
    plural: 'Risk Categories',
    addButton: 'Add Risk Category',
    editButton: 'Edit Risk Category',
    deleteButton: 'Delete Risk Category',
    
    // Custom labels for risk categories
    pageTitle: 'Risk Category Management',
    pageSubtitle: 'Manage risk categories for risk assessment classification',
    searchPlaceholder: 'Search risk categories by name...',
    
    // Form labels
    basicInfoSection: 'Risk Category Information',
    
    // Action labels
    activateRiskCategory: 'Activate Risk Category',
    deactivateRiskCategory: 'Deactivate Risk Category',
    reorderRiskCategories: 'Reorder Risk Categories'
  },

  // Fields to remove during data preparation for submission (used by BaseService)
  fieldsToRemove: [
    // Remove system fields that should not be updated
    'id',
    'createdAt',
    'updatedAt', 
    'createdById',
    'updatedById',
    // Remove all relation fields
    'riskAssessments'
  ],

  // Export filename for data exports
  exportFileName: 'risk-categories',

  // Data transformation function for preparing data before API submission
  transformData: (data) => {
    const transformed = { ...data };
    
    // Convert record_order to integer or remove if empty
    if (transformed.record_order !== undefined) {
      if (transformed.record_order === '' || transformed.record_order === null) {
        // Remove empty record_order field entirely
        delete transformed.record_order;
      } else {
        // Convert to integer
        const numValue = parseInt(transformed.record_order, 10);
        if (!isNaN(numValue)) {
          transformed.record_order = numValue;
        } else {
          delete transformed.record_order; // Remove if not a valid number
        }
      }
    }
    
    return transformed;
  },

  // Validation function for real-time field validation (dynamically assigned)
  validateField: null // Will be set after validation module loads
};
